import { Observable } from 'rxjs/Observable';
import { map, timeout, catchError, first, take, tap, finalize } from 'rxjs/operators';
import { Inject, Injectable, OnInit } from '@angular/core';
import { FeatureTogglesDao } from '../daos/feature-toggles.dao';
import { FeatureFlags } from '../model/featureflags/feature-flags.model';
import { FeatureFlagsBuilder } from './builders/feature-flags.builder';
import { IAppConfig, IAppState, Actions } from '../../store';
import { NgRedux } from '@angular-redux/store';
// Do not import from the barrel; results in error
import { LogService } from '../services/logging.service';
import { AppAction } from '../../store/interfaces/IAppState';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { FeatureFlag } from '../model/featureflags/feature-flag.model';
import { SessionStorageService } from 'ngx-webstorage';
import { WindowRef } from '../../common/window.provider';
import { APP_CONFIG } from '../config/app.config';
import { useDefault } from '../../common/common';
import { interval } from 'rxjs/observable/interval';

const CUSTOM_FLAGS_KEY = 'customFeatureToggles-stay-mgmt';
const TARGET_FLAGS_KEY = 'targetFeatureToggles-stay-mgmt';
const DEFAULT_FLAGS_TIMEOUT = 2000;
const DEFAULT_POLLING_INTERVAL = 50;

@Injectable()
export class FeatureFlagsService implements Resolve<any> {

  private _featureFlags: FeatureFlags;
  private CLASS_NAME = 'FeatureFlagsService';
  private _flagsTimeout: number;

  constructor(
    @Inject(APP_CONFIG) private _appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _actions: Actions,
    private _logService: LogService,
    private _featureTogglesDao: FeatureTogglesDao,
    private _sessionStorage: SessionStorageService,
    private _window: WindowRef
  ) {
    this._featureFlags = new FeatureFlags();
    this._flagsTimeout = this.getFlagsTimeout();
    this._actions.dispatchAddFeatureFlags(AppAction.GET_FEATURE_FLAGS, this._featureFlags);
  }

  private getFlagsTimeout(): number {
    return useDefault(this._appConfig.featureTogglesDAO.timeout, DEFAULT_FLAGS_TIMEOUT);
  }

  // overriding the resolve function so this service becomes a Resolver
  resolve(route: ActivatedRouteSnapshot, rstate: RouterStateSnapshot): Observable<any> {
    return this.fetchFeatureFlags();
  }

  fetchFeatureFlags(): Observable<FeatureFlags> {
    this._actions.dispatchAddFeatureFlags(AppAction.GET_FEATURE_FLAGS, this._featureFlags);

    const featureFlagObservable = this._getFreshFlags();

    featureFlagObservable.subscribe(
      flags => {
        this._actions.dispatchAddFeatureFlags(AppAction.GET_FEATURE_FLAGS, flags);

        return flags;
      },
      e => {
        this._logService.error('Error retrieving feature flags from feature toggles dao', this.CLASS_NAME, e.json());
        this._actions.dispatchAddFeatureFlags(AppAction.GET_FEATURE_FLAGS, null, e);
      }
    );

    return featureFlagObservable;

  }

  private _getFreshFlags(): Observable<FeatureFlags> {
    const storedFlags = this._sessionStorage.retrieve(CUSTOM_FLAGS_KEY);
    if (storedFlags) {
      return Observable.of(new FeatureFlagsBuilder().withFeatureTogglesData(storedFlags).build());
    }

    const targetFlagsSource$ = this._getTargetFlags();
    const serviceFlagsSource$ = this._featureTogglesDao.get().pipe(
      catchError((error) => {
        this._logService.error(`Error when retrieving from featureTogglesDAO`, this.CLASS_NAME, error);
        return Observable.of({});
      })
    );

    let rawServiceFlags, rawTargetFlags;
    serviceFlagsSource$.subscribe((v) => rawServiceFlags = v);
    targetFlagsSource$.subscribe((v) => rawTargetFlags = v);

    const combinedFlagsSource$ = interval(DEFAULT_POLLING_INTERVAL).pipe(
      map(() => rawServiceFlags !== undefined && rawTargetFlags !== undefined),
      first((v) => v),
      map((v) => {
        for (const k of Object.keys(rawTargetFlags)) {
          rawServiceFlags[k] = rawTargetFlags[k];
        }
        return rawServiceFlags;
      }),
      map((v) => new FeatureFlagsBuilder().withFeatureTogglesData(v).build()),
      catchError((error) => {
        this._logService.error(`Error awaiting feature flags source emissions`, this.CLASS_NAME, error);
        return Observable.of(new FeatureFlagsBuilder().build());
      })
    );

    return combinedFlagsSource$;
  }

  private _getTargetFlags(): Observable<any> {
    // const start = Date.now();
    // return interval(DEFAULT_POLLING_INTERVAL).pipe(
    //   map(() => this._window.nativeWindow[TARGET_FLAGS_KEY]),
    //   first((v) => v !== undefined),
    //   timeout(this._flagsTimeout),
    //   catchError(() => Observable.of({}))
    // )
    // TODO: clarkaa/leetu: remove GRS v2 res override once GRS v2 prod roll-out is complete
    return Observable.of({
      'services.availabilityV2':'0.1.0',
      'services.reservationsV2':'0.1.0'
    })
  }

  checkFeatureFlag(name: string, checkVersion: string, flags: Array<FeatureFlag>) {
    if (!flags) {
      return false;
    }

    const found = flags.find(function (element) {
      return element.name === name;
    });
    if (found && found.isActive) {
      return this.compareVersionNumbers(found.version, checkVersion) >= 0;
    }
    return false;
  }

  /**
   * Compare two software version numbers (e.g. 1.7.1)
   * Returns:
   *
   *  0 if they're identical
   *  -1 if v1 < v2
   *  1 if v1 > v2
   *  Nan if they in the wrong format
   *
   *  Taken from http://stackoverflow.com/a/6832721/11236
   */

  compareVersionNumbers(v1, v2): Number {
    const v1parts = v1.split('.');
    const v2parts = v2.split('.');

    function isPositiveInteger(x) {
      return /^\d+$/.test(x);
    }

    // First, validate both numbers are true version numbers
    function validateParts(parts) {
      for (let i = 0; i < parts.length; ++i) {
        if (!isPositiveInteger(parts[i])) {
          return false;
        }
      }
      return true;
    }
    if (!validateParts(v1parts) || !validateParts(v2parts)) {
      return NaN;
    }

    for (let i = 0; i < v1parts.length; ++i) {
      if (v2parts.length === i) {
        return 1;
      }

      if (v1parts[i] === v2parts[i]) {
        continue;
      }
      if (parseInt(v1parts[i]) > parseInt(v2parts[i])) {
        return 1;
      }
      return -1;
    }

    if (v1parts.length != v2parts.length) {
      return -1;
    }

    return 0;
  }

};



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/feature-flags.service.ts